import type {
  BytecodeInstruction,
  ClassInfo,
  CrossReference,
  DataFlowStep,
  DataFlowTrace,
  DataSinkKind,
  DataSourceKind,
  EntryPoint,
  RecoveredExpression,
  Severity,
} from "./types";
import {
  dataTypesForSource,
  destinationsFromValues,
  techniquesForFlow,
} from "./attack-mapping";

interface ParsedReference {
  owner: string;
  method: string;
  args: string[];
  returns: string;
}

interface Origin {
  kind: DataSourceKind;
  label: string;
  step: Omit<DataFlowStep, "index">;
}

interface SymbolicValue {
  values: string[];
  expression: string;
  origins: Origin[];
  params: number[];
  transforms: string[];
  steps: Array<Omit<DataFlowStep, "index">>;
  sinkHints: DataSinkKind[];
  builderParts?: string[];
  heapId?: string;
}

interface SinkEvent {
  kind: DataSinkKind;
  label: string;
  instruction: BytecodeInstruction;
  value: SymbolicValue;
}

interface MethodSummary {
  returnValue: SymbolicValue;
  sinks: SinkEvent[];
  recovered: RecoveredExpression[];
}

interface MethodContext {
  classInfo: ClassInfo;
  methodName: string;
  methodDescriptor: string;
  entry: string;
}

const MAX_VALUES = 12;
const MAX_STEPS = 18;
const MAX_TRACES = 400;
const MAX_RECOVERED = 2_000;
const MAX_ORIGINS = 16;
const MAX_PARAMS = 16;
const MAX_TRANSFORMS = 16;
const MAX_SINK_HINTS = 8;
const MAX_SINKS_PER_METHOD = 48;
const MAX_STACK_DEPTH = 256;
const MAX_VALUE_LENGTH = 4_096;
const MAX_EXPRESSION_LENGTH = 640;

function boundedText(value: string, limit: number) {
  return value.length <= limit ? value : `${value.slice(0, limit - 1)}…`;
}

function boundedValues(values: string[]) {
  return unique(values.map((value) => boundedText(value, MAX_VALUE_LENGTH))).slice(
    0,
    MAX_VALUES,
  );
}

function unique<T>(values: T[]) {
  return Array.from(new Set(values));
}

function blank(expression = "?"): SymbolicValue {
  return {
    values: [],
    expression: boundedText(expression, MAX_EXPRESSION_LENGTH),
    origins: [],
    params: [],
    transforms: [],
    steps: [],
    sinkHints: [],
  };
}

function copy(value: SymbolicValue): SymbolicValue {
  return {
    ...value,
    values: boundedValues(value.values),
    expression: boundedText(value.expression, MAX_EXPRESSION_LENGTH),
    origins: [...value.origins],
    params: [...value.params],
    transforms: [...value.transforms],
    steps: [...value.steps],
    sinkHints: [...value.sinkHints],
    builderParts: value.builderParts ? [...value.builderParts] : undefined,
    heapId: value.heapId,
  };
}

function merge(
  values: SymbolicValue[],
  expression?: string,
): SymbolicValue {
  const mergedExpression =
    expression ??
    values
      .map((value) => boundedText(value.expression, 160))
      .join(" + ");
  return {
    values: boundedValues(values.flatMap((value) => value.values)),
    expression: boundedText(mergedExpression, MAX_EXPRESSION_LENGTH),
    origins: uniqueBy(
      values.flatMap((value) => value.origins),
      (origin) => `${origin.kind}:${origin.label}`,
    ).slice(0, MAX_ORIGINS),
    params: unique(values.flatMap((value) => value.params)).slice(
      0,
      MAX_PARAMS,
    ),
    transforms: unique(values.flatMap((value) => value.transforms)).slice(
      0,
      MAX_TRANSFORMS,
    ),
    steps: uniqueBy(
      values.flatMap((value) => value.steps),
      (step) =>
        `${step.entry}:${step.method}:${step.offset ?? -1}:${step.operation}`,
    ).slice(-MAX_STEPS),
    sinkHints: unique(values.flatMap((value) => value.sinkHints)).slice(
      0,
      MAX_SINK_HINTS,
    ),
    heapId: values.find((value) => value.heapId)?.heapId,
  };
}

function uniqueBy<T>(values: T[], key: (value: T) => string) {
  const seen = new Set<string>();
  return values.filter((value) => {
    const id = key(value);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function splitArgs(value: string) {
  if (!value.trim()) return [];
  const args: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "<" || character === "(" || character === "[") depth += 1;
    if (character === ">" || character === ")" || character === "]")
      depth = Math.max(0, depth - 1);
    if (character === "," && depth === 0) {
      args.push(value.slice(start, index).trim());
      start = index + 1;
    }
  }
  args.push(value.slice(start).trim());
  return args.filter(Boolean);
}

function parseReference(reference?: string): ParsedReference | null {
  if (!reference) return null;
  const arrow = reference.lastIndexOf("→");
  const call = (arrow >= 0 ? reference.slice(0, arrow) : reference).trim();
  const returns = arrow >= 0 ? reference.slice(arrow + 1).trim() : "?";
  const open = call.indexOf("(");
  const close = call.lastIndexOf(")");
  const beforeArgs = open >= 0 ? call.slice(0, open) : call;
  const separator = beforeArgs.lastIndexOf(".");
  if (separator < 0) return null;
  return {
    owner: beforeArgs.slice(0, separator).replaceAll("/", "."),
    method: beforeArgs.slice(separator + 1),
    args:
      open >= 0 && close > open
        ? splitArgs(call.slice(open + 1, close))
        : [],
    returns,
  };
}

function methodKey(className: string, method: string) {
  return `${className}#${method}`;
}

function instructionStep(
  context: MethodContext,
  instruction: BytecodeInstruction,
  role: DataFlowStep["role"],
  operation: string,
  detail: string,
): Omit<DataFlowStep, "index"> {
  return {
    role,
    entry: context.entry,
    className: context.classInfo.name,
    method: context.methodName,
    offset: instruction.offset,
    operation,
    detail,
  };
}

function localIndex(instruction: BytecodeInstruction) {
  const implicit = instruction.mnemonic.match(/_(\d)$/)?.[1];
  if (implicit) return Number(implicit);
  const explicit = instruction.operands.match(/(?:local\s+)?(\d+)/)?.[1];
  return explicit ? Number(explicit) : 0;
}

function isVoid(type: string) {
  return /(?:^|\s)void$/i.test(type);
}

function sourceForReference(
  parsed: ParsedReference,
): { kind: DataSourceKind; label: string } | null {
  const value = `${parsed.owner}.${parsed.method}`.toLowerCase();
  if (/minecraft.*(?:session).*getaccesstoken|session\.getaccesstoken/.test(value))
    return {
      kind: "minecraft-access-token",
      label: "live Minecraft access token",
    };
  if (/minecraft.*(?:session).*get(?:user)?name|session\.getusername/.test(value))
    return { kind: "minecraft-username", label: "Minecraft username" };
  if (/minecraft.*(?:session).*getuuid|session\.getuuid/.test(value))
    return { kind: "minecraft-uuid", label: "Minecraft UUID" };
  if (/minecraft.*getsession/.test(value))
    return { kind: "minecraft-session", label: "live Minecraft session" };
  if (/system\.getenv/.test(value))
    return { kind: "environment", label: "environment variable" };
  if (/system\.getproperty/.test(value))
    return { kind: "system-property", label: "system property" };
  if (
    /files\.(?:readstring|readallbytes|readlines)|fileinputstream|bufferedreader/.test(
      value,
    )
  )
    return { kind: "file-content", label: "local file content" };
  if (/url\.openstream|http.*getinputstream|httpclient\.send/.test(value))
    return { kind: "remote-content", label: "downloaded remote content" };
  if (/getsystemclipboard|getcontents/.test(value))
    return { kind: "clipboard", label: "clipboard content" };
  if (/createscreencapture/.test(value))
    return { kind: "screen", label: "screen capture" };
  if (/launcher.*account|launcher_accounts|launcher_profiles/.test(value))
    return { kind: "launcher-account", label: "launcher account data" };
  return null;
}

function sinkForReference(
  parsed: ParsedReference,
  receiver: SymbolicValue | undefined,
  args: SymbolicValue[],
): { kind: DataSinkKind; label: string; value: SymbolicValue } | null {
  const value = `${parsed.owner}.${parsed.method}`.toLowerCase();
  const combined = merge([...(receiver ? [receiver] : []), ...args]);
  if (/runtime\.exec|processbuilder\.(?:start|<init>)/.test(value))
    return {
      kind: "process-execution",
      label: "operating-system process execution",
      value: combined,
    };
  if (/classloader\.defineclass|lookup\.defineclass|class\.forname/.test(value))
    return {
      kind: "dynamic-class-load",
      label: "dynamic class loading",
      value: combined,
    };
  if (/files\.(?:write|writestring|copy)|fileoutputstream\.write/.test(value))
    return {
      kind: "file-write",
      label: "file write",
      value: combined,
    };
  if (/sendchatcommand|sendcommand|sendchatmessage/.test(value))
    return {
      kind: "chat-command",
      label: "Minecraft chat command",
      value: combined,
    };
  if (
    /httpclient\.send|call\.execute|okhttpclient\.newcall|urlconnection\.connect/.test(
      value,
    )
  )
    return {
      kind: "http-request",
      label: "outbound HTTP request",
      value: combined,
    };
  if (/socket.*(?:write|send)|datagrampacket/.test(value))
    return {
      kind: "socket-write",
      label: "outbound socket write",
      value: combined,
    };
  if (/(?:outputstream|writer)\.write/.test(value)) {
    if (receiver?.sinkHints.includes("http-request"))
      return {
        kind: "http-request",
        label: "HTTP request body write",
        value: combined,
      };
    if (receiver?.sinkHints.includes("socket-write"))
      return {
        kind: "socket-write",
        label: "socket output write",
        value: combined,
      };
  }
  if (/clipboard\.setcontents/.test(value))
    return {
      kind: "clipboard-write",
      label: "clipboard replacement",
      value: combined,
    };
  return null;
}

function sinkHint(parsed: ParsedReference): DataSinkKind[] {
  const value = `${parsed.owner}.${parsed.method}`.toLowerCase();
  if (/http.*getoutputstream|urlconnection\.getoutputstream/.test(value))
    return ["http-request"];
  if (/socket.*getoutputstream/.test(value)) return ["socket-write"];
  return [];
}

function suspiciousLiteral(value: string) {
  return (
    /(?:access.?token|refresh.?token|password|cookie|webhook|\/pay\b|powershell|cmd\.exe|https?:\/\/|launcher_accounts|login data)/i.test(
      value,
    ) || /^[a-f0-9]{32,}$/i.test(value)
  );
}

function decodeBase64(value: string) {
  const clean = value.replace(/\s+/g, "");
  if (clean.length < 8 || clean.length % 4 !== 0 || !/^[A-Za-z0-9+/]+=*$/.test(clean))
    return null;
  try {
    const decoded = globalThis.atob(clean);
    if (!decoded || [...decoded].some((char) => {
      const code = char.charCodeAt(0);
      return code < 9 || (code > 13 && code < 32);
    }))
      return null;
    return decoded;
  } catch {
    return null;
  }
}

function decodeHex(value: string) {
  const clean = value.replace(/^0x/i, "").replace(/\s+/g, "");
  if (clean.length < 8 || clean.length % 2 || !/^[a-f0-9]+$/i.test(clean))
    return null;
  const bytes = clean.match(/.{2}/g)?.map((pair) => Number.parseInt(pair, 16));
  if (!bytes) return null;
  const decoded = String.fromCharCode(...bytes);
  return /^[\x09\x0a\x0d\x20-\x7e]+$/.test(decoded) ? decoded : null;
}

function transformCall(
  parsed: ParsedReference,
  receiver: SymbolicValue | undefined,
  args: SymbolicValue[],
  context: MethodContext,
  instruction: BytecodeInstruction,
): SymbolicValue | null {
  const call = `${parsed.owner}.${parsed.method}`.toLowerCase();
  const inputs = [...(receiver ? [receiver] : []), ...args];
  const combined = merge(inputs);
  const step = instructionStep(
    context,
    instruction,
    "transform",
    parsed.method,
    instruction.reference ?? `${parsed.owner}.${parsed.method}`,
  );

  if (/stringbuilder\.<init>|stringbuffer\.<init>/.test(call)) {
    const parts = args.flatMap((value) => value.values);
    return {
      ...combined,
      expression: `new StringBuilder(${args[0]?.expression ?? ""})`,
      builderParts: parts,
      steps: [...combined.steps, step].slice(-MAX_STEPS),
      transforms: unique([...combined.transforms, "StringBuilder"]),
    };
  }
  if (/stringbuilder\.append|stringbuffer\.append/.test(call)) {
    const parts = [
      ...(receiver?.builderParts ?? receiver?.values ?? []),
      ...args.flatMap((value) => value.values),
    ].slice(0, MAX_VALUES);
    const joined = boundedText(parts.join(""), MAX_VALUE_LENGTH);
    return {
      ...combined,
      values: joined ? [joined] : combined.values,
      expression: `${receiver?.expression ?? "builder"}.append(${args
        .map((value) => value.expression)
        .join(", ")})`,
      builderParts: parts,
      transforms: unique([...combined.transforms, "StringBuilder"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/stringbuilder\.tostring|stringbuffer\.tostring/.test(call)) {
    const joined = boundedText(
      receiver?.builderParts?.join("") ?? receiver?.values.join("") ?? "",
      MAX_VALUE_LENGTH,
    );
    return {
      ...combined,
      values: joined ? [joined] : combined.values,
      expression: `${receiver?.expression ?? "builder"}.toString()`,
      transforms: unique([...combined.transforms, "StringBuilder"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/string\.concat/.test(call)) {
    const joined = boundedText(
      inputs.flatMap((value) => value.values).join(""),
      MAX_VALUE_LENGTH,
    );
    return {
      ...combined,
      values: joined ? [joined] : combined.values,
      expression: inputs.map((value) => value.expression).join(" + "),
      transforms: unique([...combined.transforms, "string concatenation"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/string\.format|messageformat\.format/.test(call)) {
    const format = args[0]?.values[0];
    let rebuilt = format;
    if (format) {
      let cursor = 1;
      rebuilt = format.replace(/%[sdxf]/g, () => args[cursor++]?.values[0] ?? "%?");
    }
    return {
      ...combined,
      values: rebuilt ? [rebuilt] : combined.values,
      expression: `format(${args.map((value) => value.expression).join(", ")})`,
      transforms: unique([...combined.transforms, "formatting"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/base64.*decode/.test(call)) {
    const decoded = args
      .flatMap((value) => value.values)
      .map(decodeBase64)
      .filter((value): value is string => Boolean(value));
    return {
      ...combined,
      values: decoded.length ? decoded : combined.values,
      expression: `Base64.decode(${args.map((value) => value.expression).join(", ")})`,
      transforms: unique([...combined.transforms, "Base64"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/parsehex|hexformat.*parsehex/.test(call)) {
    const decoded = args
      .flatMap((value) => value.values)
      .map(decodeHex)
      .filter((value): value is string => Boolean(value));
    return {
      ...combined,
      values: decoded.length ? decoded : combined.values,
      expression: `hex(${args.map((value) => value.expression).join(", ")})`,
      transforms: unique([...combined.transforms, "hex"]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  if (/cipher\.dofinal|decrypt|decode|unpack/.test(call)) {
    return {
      ...combined,
      expression: `${parsed.method}(${args.map((value) => value.expression).join(", ")})`,
      transforms: unique([...combined.transforms, parsed.method]),
      steps: [...combined.steps, step].slice(-MAX_STEPS),
    };
  }
  return null;
}

function parameterCount(descriptor: string) {
  const open = descriptor.indexOf("(");
  const close = descriptor.indexOf(")");
  return open >= 0 && close > open
    ? splitArgs(descriptor.slice(open + 1, close)).length
    : 0;
}

function dynamicArgumentCount(reference?: string) {
  if (!reference) return 2;
  const label = reference.split("·").at(-1)?.trim() ?? reference;
  const open = label.indexOf("(");
  const close = label.indexOf(")", open + 1);
  if (open < 0 || close < open) return 2;
  return splitArgs(label.slice(open + 1, close)).length;
}

function isContainerMutation(parsed: ParsedReference) {
  const call = `${parsed.owner}.${parsed.method}`.toLowerCase();
  return /(?:jsonobject|jsonobject|jsonnode|objectnode|map)\.(?:addproperty|add|put|set|replace)|formbody\$builder\.add/.test(
    call,
  );
}

function evaluateMethod(
  classInfo: ClassInfo,
  method: ClassInfo["methods"][number],
  summaries: Map<string, MethodSummary>,
  internalClasses: Set<string>,
): MethodSummary {
  const context: MethodContext = {
    classInfo,
    methodName: method.name,
    methodDescriptor: method.descriptor,
    entry: classInfo.path,
  };
  const stack: SymbolicValue[] = [];
  const locals = new Map<number, SymbolicValue>();
  const heap = new Map<string, SymbolicValue>();
  const isStatic = method.access.includes("static");
  let local = isStatic ? 0 : 1;
  for (let param = 0; param < parameterCount(method.descriptor); param += 1) {
    locals.set(local, {
      ...blank(`param${param}`),
      params: [param],
      origins: [
        {
          kind: "method-parameter",
          label: `method parameter ${param + 1}`,
          step: {
            role: "source",
            entry: classInfo.path,
            className: classInfo.name,
            method: method.name,
            operation: "parameter",
            detail: `Parameter ${param + 1}`,
          },
        },
      ],
    });
    local += 1;
  }

  const sinks: SinkEvent[] = [];
  const recovered: RecoveredExpression[] = [];
  let returned = blank("return");
  const pop = () => stack.pop() ?? blank();
  const push = (...values: SymbolicValue[]) => {
    for (const value of values) {
      if (stack.length >= MAX_STACK_DEPTH) stack.shift();
      stack.push(value);
    }
  };

  for (const instruction of method.instructions) {
    const mnemonic = instruction.mnemonic;
    if (
      mnemonic === "ldc" ||
      mnemonic === "ldc_w" ||
      mnemonic === "ldc2_w" ||
      /^(?:iconst|lconst|fconst|dconst|bipush|sipush)/.test(mnemonic)
    ) {
      const literal =
        instruction.literal ??
        (/^-?\d+$/.test(instruction.operands)
          ? Number(instruction.operands)
          : instruction.reference);
      const stringValue = literal === undefined ? "" : String(literal);
      const step = instructionStep(
        context,
        instruction,
        "source",
        "constant",
        JSON.stringify(stringValue),
      );
      push({
        ...blank(JSON.stringify(literal)),
        values: stringValue ? [stringValue] : [],
        origins:
          typeof literal === "string" && suspiciousLiteral(literal)
            ? [{ kind: "hardcoded-value", label: literal.slice(0, 160), step }]
            : [],
        steps: [step],
      });
      continue;
    }
    if (/^[a-z]load(?:_\d)?$/.test(mnemonic)) {
      push(copy(locals.get(localIndex(instruction)) ?? blank(`local${localIndex(instruction)}`)));
      continue;
    }
    if (/^[a-z]store(?:_\d)?$/.test(mnemonic)) {
      locals.set(localIndex(instruction), copy(pop()));
      continue;
    }
    if (mnemonic === "dup") {
      push(copy(stack.at(-1) ?? blank()));
      continue;
    }
    if (mnemonic === "swap" && stack.length >= 2) {
      const right = pop();
      const left = pop();
      push(right, left);
      continue;
    }
    if (mnemonic === "pop" || mnemonic === "pop2") {
      pop();
      if (mnemonic === "pop2") pop();
      continue;
    }
    if (mnemonic === "new") {
      const heapId = `${classInfo.name}#${method.name}@${instruction.offset}`;
      const allocated = {
        ...blank(`new ${instruction.reference ?? instruction.operands}`),
        heapId,
      };
      heap.set(heapId, copy(allocated));
      push(allocated);
      continue;
    }
    if (/^(?:getstatic|getfield)$/.test(mnemonic)) {
      const receiver = mnemonic === "getfield" ? pop() : blank();
      push({
        ...receiver,
        expression: instruction.reference ?? instruction.operands,
      });
      continue;
    }
    if (/^(?:putstatic|putfield)$/.test(mnemonic)) {
      pop();
      if (mnemonic === "putfield") pop();
      continue;
    }
    if (/^(?:a|i|l|f|d)aload$/.test(mnemonic)) {
      const indexValue = pop();
      const array = pop();
      push(merge([array, indexValue], `${array.expression}[${indexValue.expression}]`));
      continue;
    }
    if (/^(?:a|i|l|f|d)astore$/.test(mnemonic)) {
      pop();
      pop();
      pop();
      continue;
    }
    if (
      /^(?:i|l|f|d)(?:add|sub|mul|div|rem|and|or|xor|shl|shr|ushr)$/.test(
        mnemonic,
      )
    ) {
      const right = pop();
      const left = pop();
      push(merge([left, right], `(${left.expression} ${mnemonic} ${right.expression})`));
      continue;
    }
    if (/^(?:invokevirtual|invokespecial|invokestatic|invokeinterface)$/.test(mnemonic)) {
      const parsed = parseReference(instruction.reference);
      if (!parsed) continue;
      const hydrate = (value: SymbolicValue) => {
        const heapValue = value.heapId ? heap.get(value.heapId) : undefined;
        return heapValue
          ? { ...merge([value, heapValue]), heapId: value.heapId }
          : value;
      };
      const args = Array.from(
        { length: parsed.args.length },
        () => pop(),
      )
        .reverse()
        .map(hydrate);
      const receiver =
        mnemonic === "invokestatic" ? undefined : hydrate(pop());
      if (receiver?.heapId && isContainerMutation(parsed)) {
        const previous = heap.get(receiver.heapId) ?? receiver;
        const updated = {
          ...merge(
            [previous, ...args],
            `${receiver.expression}.${parsed.method}(${args
              .map((value) => value.expression)
              .join(", ")})`,
          ),
          heapId: receiver.heapId,
        };
        updated.transforms = unique([
          ...updated.transforms,
          /json/i.test(parsed.owner) ? "JSON serialization" : "container",
        ]);
        updated.steps = [
          ...updated.steps,
          instructionStep(
            context,
            instruction,
            "transform",
            parsed.method,
            instruction.reference ?? `${parsed.owner}.${parsed.method}`,
          ),
        ].slice(-MAX_STEPS);
        heap.set(receiver.heapId, updated);
      }
      const internal = internalClasses.has(parsed.owner)
        ? summaries.get(methodKey(parsed.owner, parsed.method))
        : undefined;
      const sink = sinkForReference(parsed, receiver, args);
      if (sink && sinks.length < MAX_SINKS_PER_METHOD) {
        sinks.push({ ...sink, instruction });
      }

      if (internal && sinks.length < MAX_SINKS_PER_METHOD) {
        for (const nestedSink of internal.sinks) {
          if (sinks.length >= MAX_SINKS_PER_METHOD) break;
          const mappedInputs = nestedSink.value.params
            .map((index) => args[index])
            .filter((value): value is SymbolicValue => Boolean(value));
          const mapped = merge([
            nestedSink.value,
            ...mappedInputs,
            ...(receiver ? [receiver] : []),
          ]);
          mapped.steps.push(
            instructionStep(
              context,
              instruction,
              "call",
              `${parsed.owner}.${parsed.method}`,
              "Interprocedural call",
            ),
          );
          sinks.push({ ...nestedSink, value: mapped });
        }
      }

      if (!isVoid(parsed.returns)) {
        const directSource = sourceForReference(parsed);
        const transformed = transformCall(
          parsed,
          receiver,
          args,
          context,
          instruction,
        );
        let result =
          transformed ??
          (internal
            ? merge(
                [
                  internal.returnValue,
                  ...internal.returnValue.params
                    .map((index) => args[index])
                    .filter((value): value is SymbolicValue => Boolean(value)),
                ],
                `${parsed.method}(…)`,
              )
            : merge([...(receiver ? [receiver] : []), ...args], `${parsed.method}(…)`));
        if (directSource) {
          const sourceStep = instructionStep(
            context,
            instruction,
            "source",
            parsed.method,
            directSource.label,
          );
          result = {
            ...result,
            origins: uniqueBy(
              [
                ...result.origins,
                { ...directSource, step: sourceStep },
              ],
              (origin) => `${origin.kind}:${origin.label}`,
            ),
            steps: [...result.steps, sourceStep].slice(-MAX_STEPS),
          };
        }
        result.sinkHints = unique([
          ...result.sinkHints,
          ...sinkHint(parsed),
        ]).slice(0, MAX_SINK_HINTS);
        if (receiver?.heapId) {
          result.heapId = receiver.heapId;
          heap.set(receiver.heapId, copy(result));
        }
        push(result);

        const rebuilt = result.values.find(
          (value) =>
            value.length >= 4 &&
            (result.transforms.length > 0 || suspiciousLiteral(value)),
        );
        if (
          rebuilt &&
          recovered.length < MAX_RECOVERED &&
          result.transforms.length > 0
        ) {
          const transform = result.transforms.includes("StringBuilder")
            ? "StringBuilder"
            : result.transforms.includes("Base64")
              ? "Base64"
              : result.transforms.includes("hex")
                ? "hex"
                : result.transforms.includes("formatting")
                  ? "formatting"
                  : result.transforms.includes("string concatenation")
                    ? "string concatenation"
                    : "interprocedural";
          recovered.push({
            id: `${classInfo.name}:${method.name}:${instruction.offset}:${recovered.length}`,
            entry: classInfo.path,
            className: classInfo.name,
            method: method.name,
            offset: instruction.offset,
            value: rebuilt.slice(0, 4096),
            expression: result.expression.slice(0, 600),
            transform,
            confidence: "high",
          });
        }
      }
      continue;
    }
    if (mnemonic === "invokedynamic") {
      const inputs = Array.from(
        { length: dynamicArgumentCount(instruction.reference) },
        () => pop(),
      ).reverse();
      const result = merge(
        inputs,
        inputs.map((value) => value.expression).join(" + "),
      );
      const joined = boundedText(
        inputs.flatMap((value) => value.values).join(""),
        MAX_VALUE_LENGTH,
      );
      if (joined) result.values = [joined];
      result.transforms.push("string concatenation");
      push(result);
      if (joined.length >= 4 && recovered.length < MAX_RECOVERED) {
        recovered.push({
          id: `${classInfo.name}:${method.name}:${instruction.offset}:indy`,
          entry: classInfo.path,
          className: classInfo.name,
          method: method.name,
          offset: instruction.offset,
          value: joined.slice(0, 4096),
          expression: result.expression.slice(0, 600),
          transform: "string concatenation",
          confidence: "medium",
        });
      }
      continue;
    }
    if (/^(?:a|i|l|f|d)return$/.test(mnemonic)) {
      returned = merge([returned, pop()], "return");
      continue;
    }
    if (
      /^(?:if|goto|tableswitch|lookupswitch|checkcast|instanceof|monitor)/.test(
        mnemonic,
      )
    ) {
      if (mnemonic.startsWith("if")) pop();
      continue;
    }
  }
  return { returnValue: returned, sinks, recovered };
}

function rootMethodNames(entryPoint: EntryPoint) {
  if (entryPoint.type === "main") return ["main", "<clinit>"];
  if (entryPoint.type === "fabric")
    return [
      "onInitialize",
      "onInitializeClient",
      "onInitializeServer",
      "<init>",
      "<clinit>",
    ];
  if (["bukkit", "paper", "velocity"].includes(entryPoint.type))
    return ["onLoad", "onEnable", "initialize", "<init>", "<clinit>"];
  return ["main", "run", "start", "initialize", "<init>", "<clinit>"];
}

function buildReachability(
  classes: ClassInfo[],
  entryPoints: EntryPoint[],
  references: CrossReference[],
) {
  const roots = new Set<string>();
  const entryLabels = new Map<string, string>();
  for (const entryPoint of entryPoints) {
    const classInfo = classes.find(
      (item) =>
        item.name === entryPoint.className ||
        item.name.replace(/\$/g, ".") === entryPoint.className,
    );
    if (!classInfo) continue;
    const preferred = rootMethodNames(entryPoint);
    const methods = classInfo.methods.filter((method) =>
      preferred.includes(method.name),
    );
    for (const method of methods.length ? methods : classInfo.methods.slice(0, 8)) {
      const key = methodKey(classInfo.name, method.name);
      roots.add(key);
      entryLabels.set(
        key,
        `${entryPoint.type} entry point ${entryPoint.className}`,
      );
    }
  }
  if (!roots.size) {
    for (const classInfo of classes) {
      for (const method of classInfo.methods.filter((item) =>
        ["main", "premain", "agentmain", "<clinit>"].includes(item.name),
      )) {
        roots.add(methodKey(classInfo.name, method.name));
        entryLabels.set(
          methodKey(classInfo.name, method.name),
          `inferred ${method.name} entry point`,
        );
      }
    }
  }
  const outgoing = new Map<string, string[]>();
  for (const reference of references.filter((item) => !item.external)) {
    const from = methodKey(reference.fromClass, reference.fromMethod);
    const to = methodKey(reference.toClass, reference.toMethod);
    outgoing.set(from, [...(outgoing.get(from) ?? []), to]);
  }
  const reachable = new Set(roots);
  const labels = new Map(entryLabels);
  const queue = [...roots];
  while (queue.length) {
    const current = queue.shift()!;
    for (const target of outgoing.get(current) ?? []) {
      if (reachable.has(target)) continue;
      reachable.add(target);
      labels.set(target, labels.get(current) ?? "reachable call path");
      queue.push(target);
    }
  }
  return { reachable, labels };
}

function severityFor(
  source: DataSourceKind,
  sink: DataSinkKind,
  reachable: boolean,
  values: string[],
): Severity {
  const credential = [
    "minecraft-access-token",
    "launcher-account",
    "browser-credential",
    "discord-token",
    "minecraft-session",
  ].includes(source);
  const exfiltration = ["http-request", "socket-write", "webhook"].includes(sink);
  if (reachable && credential && exfiltration) return "critical";
  if (
    reachable &&
    source === "remote-content" &&
    ["process-execution", "dynamic-class-load"].includes(sink)
  )
    return "critical";
  if (
    reachable &&
    sink === "chat-command" &&
    values.some((value) => /(?:^|\s)\/?pay\s+\S+\s+\S+/i.test(value))
  )
    return "critical";
  if (credential && exfiltration) return "high";
  if (
    ["process-execution", "dynamic-class-load", "chat-command"].includes(sink)
  )
    return reachable ? "high" : "medium";
  return reachable ? "medium" : "low";
}

function titleFor(source: DataSourceKind, sink: DataSinkKind) {
  if (
    source === "minecraft-access-token" &&
    ["http-request", "socket-write", "webhook"].includes(sink)
  )
    return "Minecraft access token reaches an outbound channel";
  if (
    source === "remote-content" &&
    ["process-execution", "dynamic-class-load"].includes(sink)
  )
    return "Downloaded content reaches execution";
  if (sink === "chat-command") return "Hardcoded data reaches a chat command";
  if (sink === "process-execution") return "Collected data reaches process execution";
  if (sink === "file-write") return "Collected data is written to disk";
  return `${source.replaceAll("-", " ")} reaches ${sink.replaceAll("-", " ")}`;
}

function orderedFlowSteps(
  origin: Origin,
  intermediate: Array<Omit<DataFlowStep, "index">>,
  sink: Omit<DataFlowStep, "index">,
) {
  return uniqueBy(
    [
      origin.step,
      ...intermediate.filter(
        (step) => step.role !== "source" && step.role !== "sink",
      ),
      sink,
    ],
    (step) =>
      `${step.entry}:${step.method}:${step.offset ?? -1}:${step.role}:${step.operation}`,
  ).slice(-MAX_STEPS);
}

export function analyzeSymbolicDataFlow(
  classes: ClassInfo[],
  entryPoints: EntryPoint[],
): {
  traces: DataFlowTrace[];
  recovered: RecoveredExpression[];
  references: CrossReference[];
  evaluatedMethods: number;
  reachableMethods: number;
  reachableMethodKeys: string[];
  entryPointLabels: Record<string, string>;
} {
  const internalClasses = new Set(classes.map((item) => item.name));
  const references: CrossReference[] = [];
  for (const classInfo of classes) {
    for (const method of classInfo.methods) {
      for (const instruction of method.instructions) {
        if (!instruction.mnemonic.startsWith("invoke")) continue;
        const parsed = parseReference(instruction.reference);
        if (!parsed) continue;
        references.push({
          id: `xref-${references.length + 1}`,
          fromClass: classInfo.name,
          fromMethod: method.name,
          fromEntry: classInfo.path,
          toClass: parsed.owner,
          toMethod: parsed.method,
          offset: instruction.offset,
          external: !internalClasses.has(parsed.owner),
          reachable: false,
        });
      }
    }
  }
  const reachability = buildReachability(classes, entryPoints, references);
  for (const reference of references) {
    reference.reachable = reachability.reachable.has(
      methodKey(reference.fromClass, reference.fromMethod),
    );
  }

  const summaries = new Map<string, MethodSummary>();
  for (let pass = 0; pass < 4; pass += 1) {
    for (const classInfo of classes) {
      for (const method of classInfo.methods) {
        summaries.set(
          methodKey(classInfo.name, method.name),
          evaluateMethod(classInfo, method, summaries, internalClasses),
        );
      }
    }
  }

  const traces: DataFlowTrace[] = [];
  const recovered: RecoveredExpression[] = [];
  const traceKeys = new Set<string>();
  for (const classInfo of classes) {
    for (const method of classInfo.methods) {
      const key = methodKey(classInfo.name, method.name);
      const summary = summaries.get(key);
      if (!summary) continue;
      recovered.push(...summary.recovered);
      for (const event of summary.sinks) {
        let origins = event.value.origins.filter(
          (origin) => origin.kind !== "method-parameter",
        );
        if (!origins.length && event.value.values.some(suspiciousLiteral)) {
          origins = [
            {
              kind: "hardcoded-value",
              label: event.value.values.find(suspiciousLiteral) ?? "hardcoded value",
              step: instructionStep(
                {
                  classInfo,
                  methodName: method.name,
                  methodDescriptor: method.descriptor,
                  entry: classInfo.path,
                },
                event.instruction,
                "source",
                "constant propagation",
                event.value.values.find(suspiciousLiteral) ?? "hardcoded value",
              ),
            },
          ];
        }
        for (const origin of origins) {
          if (traces.length >= MAX_TRACES) break;
          const reachable = reachability.reachable.has(key);
          const values = event.value.values.filter((value) => value.length <= 4096);
          const traceKey = `${origin.kind}:${event.kind}:${classInfo.name}:${method.name}:${values.join("|")}`;
          if (traceKeys.has(traceKey)) continue;
          traceKeys.add(traceKey);
          const sinkStep = instructionStep(
            {
              classInfo,
              methodName: method.name,
              methodDescriptor: method.descriptor,
              entry: classInfo.path,
            },
            event.instruction,
            "sink",
            event.kind,
            event.label,
          );
          const rawSteps = orderedFlowSteps(
            origin,
            event.value.steps,
            sinkStep,
          );
          const destinations = destinationsFromValues(values);
          const techniques = techniquesForFlow(
            origin.kind,
            event.kind,
            values,
          );
          traces.push({
            id: `flow-${traces.length + 1}`,
            severity: severityFor(origin.kind, event.kind, reachable, values),
            confidence: reachable ? "high" : "medium",
            title: titleFor(origin.kind, event.kind),
            description: reachable
              ? "A bounded JVM stack evaluation found a concrete, entry-point-reachable path from the source to the sink."
              : "A bounded JVM stack evaluation found a source-to-sink path, but no supported archive entry point reached the containing method.",
            sourceKind: origin.kind,
            sinkKind: event.kind,
            sourceLabel: origin.label,
            sinkLabel: event.label,
            entry: classInfo.path,
            method: method.name,
            reachable,
            entryPoint: reachability.labels.get(key),
            ordering:
              origin.step.offset !== undefined &&
              sinkStep.offset !== undefined
                ? "proven"
                : "partial",
            dataTypes: dataTypesForSource(origin.kind),
            destinations,
            recoveredValues: unique(values).slice(0, 8),
            steps: rawSteps.slice(-MAX_STEPS).map((step, index) => ({
              ...step,
              index: index + 1,
            })),
            techniques,
            tags: unique([
              "data-flow",
              origin.kind,
              event.kind,
              reachable ? "entry-point-reachable" : "unproven-reachability",
              destinations.length ? "destination-resolved" : "",
              ...techniques.map((item) => item.id.toLowerCase()),
            ]).filter(Boolean),
          });
        }
      }
    }
  }
  traces.sort((left, right) => {
    const order: Record<Severity, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
      info: 4,
    };
    return order[left.severity] - order[right.severity];
  });
  return {
    traces,
    recovered: uniqueBy(
      recovered,
      (item) => `${item.entry}:${item.method}:${item.offset}:${item.value}`,
    ).slice(0, MAX_RECOVERED),
    references,
    evaluatedMethods: summaries.size,
    reachableMethods: reachability.reachable.size,
    reachableMethodKeys: [...reachability.reachable],
    entryPointLabels: Object.fromEntries(reachability.labels),
  };
}
